import { useEffect, useState } from 'react'
import { ROLE } from '@ovrsee/shared'
import { useSearchParams } from 'react-router'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Topbar } from '../../components/Topbar.js'
import { useAuth } from '../../lib/auth.js'
import { usePageTitle } from '../../lib/page-title.js'
import { Button, Input, Select, TextArea } from '../../ui/index.js'
import {
  useCreateLeave,
  useLeaveDetail,
  useLeaveList,
  useOverlappingTasks,
  useReviewLeave,
  useWithdrawLeave,
  type LeaveItem,
} from './useLeave.js'

function LeaveCard({ item, onOpen }: { item: LeaveItem; onOpen: () => void }) {
  const withdrawLeave = useWithdrawLeave(item.id)
  const canWithdraw = item.status === 'PENDING'
  return (
    <article className="page-section">
      <div className="row-between">
        <strong>{item.type.toLowerCase()}</strong>
        <span>{item.status.toLowerCase()}</span>
      </div>
      <div>
        {item.startsAt.slice(0, 10)} to {item.endsAt.slice(0, 10)}
      </div>
      {item.note !== null ? <div>{item.note}</div> : null}
      {item.decisionNote !== null ? <div>Decision note: {item.decisionNote}</div> : null}
      <div className="row-actions">
        <Button kind="secondary" size="small" type="button" onClick={onOpen}>
          Open
        </Button>
        {canWithdraw ? (
          <Button kind="tertiary" size="small" type="button" onClick={() => void withdrawLeave.mutateAsync()}>
            Withdraw
          </Button>
        ) : null}
      </div>
    </article>
  )
}

export function Leave() {
  usePageTitle('Leave')
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const managerial = user !== null && user.role !== ROLE.EMPLOYEE
  const linkedLeaveId = searchParams.get('leave')
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get('leave'))
  const list = useLeaveList()
  const [type, setType] = useState<LeaveItem['type']>('ANNUAL')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [note, setNote] = useState('')
  const [decisionNote, setDecisionNote] = useState('')
  const [extendTaskIds, setExtendTaskIds] = useState<string[]>([])
  const [extendTaskIdsTouched, setExtendTaskIdsTouched] = useState(false)

  const items = list.data?.items ?? []
  const resolvedSelectedId = linkedLeaveId ?? selectedId ?? (managerial && items.length === 1 ? items[0]!.id : null)
  const detail = useLeaveDetail(resolvedSelectedId)
  const overlaps = useOverlappingTasks(resolvedSelectedId)
  const createLeave = useCreateLeave()
  const reviewLeave = useReviewLeave(resolvedSelectedId ?? '')
  const selected = detail.data ?? null
  const overlappingTasks = overlaps.data ?? []
  const defaultExtendTaskIds = overlaps.data?.filter((task) => task.collides).map((task) => task.id) ?? []
  const visibleExtendTaskIds = extendTaskIdsTouched ? extendTaskIds : defaultExtendTaskIds

  useEffect(() => {
    setExtendTaskIds([])
    setExtendTaskIdsTouched(false)
  }, [resolvedSelectedId])

  async function submitLeave() {
    await createLeave.mutateAsync({
      type,
      startDate,
      endDate,
      ...(note.trim() === '' ? {} : { note: note.trim() }),
    })
    setNote('')
  }

  return (
    <>
      <Topbar title="Leave" eyebrow={managerial ? 'Review requests and workload' : 'Request time away'} />
      <div className="page">
        {!managerial ? (
          <section className="page-section">
            <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
              <label className="field-label" htmlFor="leave-type">
                Type
              </label>
              <Select
                id="leave-type"
                value={type}
                onChange={(value) => setType(value as LeaveItem['type'])}
                aria-label="Type"
                style={{
                  width: '100%',
                  minWidth: 0,
                  padding: '7px 10px',
                  borderRadius: 'var(--r-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  color: 'var(--fg)',
                  fontSize: 13,
                  boxSizing: 'border-box',
                }}
              >
                <option value="ANNUAL">Annual</option>
                <option value="SICK">Sick</option>
                <option value="UNPAID">Unpaid</option>
                <option value="OTHER">Other</option>
              </Select>
            </div>
            <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
              <label className="field-label" htmlFor="leave-start">
                Start date
              </label>
              <Input id="leave-start" type="date" value={startDate} onChange={setStartDate} aria-label="Start date" />
            </div>
            <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
              <label className="field-label" htmlFor="leave-end">
                End date
              </label>
              <Input id="leave-end" type="date" value={endDate} onChange={setEndDate} aria-label="End date" />
            </div>
            <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
              <label className="field-label" htmlFor="leave-note">
                Note
              </label>
              <TextArea
                id="leave-note"
                value={note}
                onChange={setNote}
                aria-label="Note"
                rows={3}
              />
            </div>
            <Button type="button" kind="primary" size="small" onClick={() => void submitLeave()}>
              Request leave
            </Button>
          </section>
        ) : null}

        <section className="page-section">
          {list.isPending ? <LoadingState label="Loading leave" /> : null}
          {list.isError ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
          {list.isSuccess && items.length === 0 ? (
            <EmptyState title={managerial ? 'No leave requests yet' : 'Nothing pending'} />
          ) : null}
          {items.map((item) => (
            <LeaveCard key={item.id} item={item} onOpen={() => setSelectedId(item.id)} />
          ))}
        </section>

        {managerial && selected !== null ? (
          <aside className="page-section">
            <h2>{selected.requester.name}</h2>
            <div>{selected.type.toLowerCase()}</div>
            <div>
              {selected.startsAt.slice(0, 10)} to {selected.endsAt.slice(0, 10)}
            </div>
            {selected.decisionNote !== null ? <div>Decision note: {selected.decisionNote}</div> : null}
            {overlappingTasks.length > 0 ? (
              <div>
                <div className="field-label">Overlapping tasks</div>
                {overlappingTasks.map((task) => (
                  <label key={task.id} className="row-between" style={{ alignItems: 'center', gap: 8 }}>
                    <span>
                      <Input
                        type="checkbox"
                        value=""
                        checked={visibleExtendTaskIds.includes(task.id)}
                        onChangeEvent={(event) =>
                          {
                            setExtendTaskIdsTouched(true)
                            setExtendTaskIds(
                              event.target.checked
                                ? [...visibleExtendTaskIds, task.id]
                                : visibleExtendTaskIds.filter((id) => id !== task.id),
                            )
                          }
                        }
                      />
                      {task.reference} · {task.title}
                    </span>
                    <span>{task.collides ? 'collides' : 'overlaps'}</span>
                  </label>
                ))}
              </div>
            ) : null}
            <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
              <label className="field-label" htmlFor="decision-note">
                Decision note
              </label>
              <TextArea
                id="decision-note"
                value={decisionNote}
                onChange={setDecisionNote}
                aria-label="Decision note"
                rows={3}
              />
            </div>
            <div className="row-actions">
              <Button
                type="button"
                kind="primary"
                size="small"
                onClick={() => void reviewLeave.mutateAsync({ decision: 'APPROVED', decisionNote, extendTaskIds: visibleExtendTaskIds })}
              >
                Approve
              </Button>
              <Button
                type="button"
                kind="secondary"
                size="small"
                onClick={() => void reviewLeave.mutateAsync({ decision: 'DECLINED', decisionNote })}
              >
                Decline
              </Button>
            </div>
          </aside>
        ) : null}
      </div>
    </>
  )
}
